// The page GROUND (2026-09-11, user call from two reference screens: "the
// gradient is used only in the AI Lab home view; when Digest / Lists is
// selected the bg is more white-like, some premium white shade"; a third
// ground the next day, from the lists reference: a deep violet gradient
// under the ranked lists). Three grounds, one attribute on the root:
//
//   (none)              PAPER — the app's default: a warm off-white page,
//                       grey cards, white brand tiles with black marks.
//   data-ground="field" FIELD — the green gradient, frosted glass over it,
//                       black tiles. ONE surface: the demo home while the
//                       Lab is its view (the Lab PAGE — the answer — is on
//                       paper too, user call the same day: "a light
//                       background there, not the gradient").
//   data-ground="dusk"  DUSK — the indigo-to-magenta gradient, white type,
//                       white marks with no tile. ONE surface: the demo
//                       home while the Lists segment is up (2026-09-12).
//   data-ground="dawn"  DAWN — the pastel sky-blue / pink / mint sweep
//                       under the WEEKLY PERSPECTIVE (2026-10-21, the
//                       user's reference screen of the one-card view —
//                       "copy it exactly"; the field's frosted surfaces,
//                       black type). A first dawn, a sky-to-peach sweep,
//                       stood under the three-card view for the one day
//                       2026-10-14 and gave way to the field.
//   data-ground="slate" SLATE — a flat dark blue-grey, white type. ONE
//                       surface: the login wall (pages/Login, 2026-10-05,
//                       from the user's reference screen). Not a gradient,
//                       but a ground of its own all the same — the root
//                       carries it so the page tone, the overscroll and
//                       the browser chrome are the wall's.
//   data-ground="mint"  MINT — a flat pale mint page with sage cards and
//                       deeper sage tiles, black type (2026-10-22, the
//                       user's desktop reference screen of the HOME
//                       dashboard). ONE surface: the flagged HOME view
//                       (pages/Home). Like the slate, no gradient layer —
//                       the page tone itself changes, so `--bg`, the root
//                       and the chrome read one colour.
//
// A screen declares its ground with this hook and the stylesheet does the
// rest (`html[data-ground="…"]` re-derives the surface tokens and fades
// the gradient layer in — index.css, the top). Layout effect, not effect:
// the attribute must be on the root BEFORE the first paint, or a screen
// opening on a gradient flashes paper for a frame. Cleanup restores paper,
// so a screen that never calls the hook is on paper by construction.
//
// The BROWSER CHROME follows the ground too (2026-09-11, user report: "on
// some older iPhones we show the bg colour at the top"). iOS Safari 15+
// paints the status-bar strip above the page from the `theme-color` meta,
// and public/index.html can only name ONE colour — the paper — so on a
// gradient the strip stayed paper over a coloured page: a pale band at
// the top. The hook keeps the meta in step with the stylesheet's
// `--chrome` token (the page tone on paper, each gradient's rendered top
// edge on the field and the dusk), so the CSS stays the one source of
// colour. The root element's own background does the same for the
// versions that paint the strip from the page, and for the rubber-band
// pull (index.css `html[data-ground=…]`).
//
// HOW the meta is updated matters on older iOS (2026-09-13, user report
// from a client's older iPhone — not the user's own: the digest, on
// paper, wearing the dusk's violet in the strip; the phone had shown the
// lists before). Safari 15 and 16 have been seen to keep the tint they
// last applied when only the `content` ATTRIBUTE of the existing meta
// changes; what they reliably read is a meta element ARRIVING. So the
// hook never edits the tag — it replaces it with a fresh one carrying
// the colour, and re-asserts it when the page comes back from the
// back-forward cache (`pageshow`), which restores the DOM as it was.
import { useLayoutEffect } from "react";

export const GROUND_FIELD = "field";
export const GROUND_PAPER = "paper";
export const GROUND_DUSK = "dusk";
export const GROUND_SLATE = "slate";
export const GROUND_DAWN = "dawn";
export const GROUND_MINT = "mint";

// the grounds the root is stamped with (paper is the absence of one)
const GRADIENTS = [
  GROUND_FIELD,
  GROUND_DUSK,
  GROUND_SLATE,
  GROUND_DAWN,
  GROUND_MINT,
];
const THEME_META = 'meta[name="theme-color"]';

// the browser chrome's colour for the ground the root currently carries:
// a FRESH meta each time the colour changes (see the head of the file),
// in the old one's place so the head keeps its order
export function paintChrome() {
  const old = document.querySelector(THEME_META);
  if (!old) return;
  const tone = getComputedStyle(document.documentElement)
    .getPropertyValue("--chrome")
    .trim();
  if (!tone || old.getAttribute("content") === tone) return;
  const meta = document.createElement("meta");
  meta.setAttribute("name", "theme-color");
  meta.setAttribute("content", tone);
  old.replaceWith(meta);
}

// a page restored from the back-forward cache comes back with the DOM as
// it was left — which may be a different ground's meta than the root
// now says (the cleanup below ran before the page went away)
const onPageShow = () => paintChrome();

// back to paper, attribute and chrome both — what every route change
// starts from (navigation/RouterConfig GroundReset, user call 2026-09-13:
// "make sure we don't add that background on any other page"): a screen
// is on paper unless it declares a ground in its own commit, whichever
// screen was up before and whether or not that one's cleanup ran first
export function resetGround() {
  delete document.documentElement.dataset.ground;
  paintChrome();
}

// `routeKey`: what makes the screen's URL its own — the brand page's slug,
// the compare screen's pair. The router resets the ground on EVERY
// pathname change (navigation/RouterConfig GroundReset), and a screen that
// stays MOUNTED across one — the compare screen re-picking its opponent,
// the brand page going brand to brand — never re-ran this effect on
// `ground` alone, so the reset won: the page kept its dusk layout on paper
// colours (user report 2026-09-28: "when the user changes who he compares
// with, old UI colours are shown"). The key in the deps re-asserts the
// ground in the same commit as the reset, after it (siblings in order).
export function useGround(ground, routeKey) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (GRADIENTS.includes(ground)) root.dataset.ground = ground;
    else delete root.dataset.ground;
    paintChrome();
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      delete root.dataset.ground;
      paintChrome();
    };
  }, [ground, routeKey]);
}

export default useGround;
